import {
  controlPaddingHorizontal,
  dropdownPaddingBlock,
  fontWeightNormal,
} from '@atlas-frontend/design-tokens';
import React, { FC, useMemo } from 'react';
import { Button, Dropdown, MenuProps } from 'antd';
import { i18nNsFactory } from '@atlas-frontend/i18n';
import { DATE_FORMAT, getDayjs, ISO_DATE_FORMAT } from '../../common/utils';
import { useDataObjectContext } from '../DataObjectContext';
import translations from '../translations/DataObjectDownload.translations.json';
import * as Icon from 'react-feather';
import { expand } from 'uri-template-lite';
import {
  AlgorithmsExcelUrl,
  AlternativesExcelUrl,
  DataObjectVersionExcelUrl,
  KeysExcelUrl,
  ValidationRulesExcelUrl,
} from '../query/DataObjectDescriptionVersionUrl';
import styled from 'styled-components';

const { addTranslations, useTranslation } = i18nNsFactory('data-object-upload');
addTranslations(translations);

export interface DataObjectDownloadProps {
  id: number;
  isInput: boolean;
  isReport: boolean;
  isIntermediate: boolean;
}

export const DataObjectDownload: FC<DataObjectDownloadProps> = ({
  id,
  isInput,
  isReport,
  isIntermediate,
}) => {
  const { t } = useTranslation();
  const {
    alternativeFilters,
    filteredAlgorithmIds,
    filters,
    requiredKeyFilters,
  } = useDataObjectContext();

  const downloadOptions: MenuProps['items'] = useMemo(() => {
    const options = [
      {
        label: (
          <DownloadButton
            id="dowload-dataObject-version-excel"
            href={expand(DataObjectVersionExcelUrl, {
              id,
            })}
            type="text"
            size="middle"
          >
            {t('download.main')}
          </DownloadButton>
        ),
        key: '1',
      },
    ];

    if (!isInput && !isIntermediate) {
      options.push({
        label: (
          <DownloadButton
            id="dowload-validation-rules-excel"
            href={expand(ValidationRulesExcelUrl, {
              id,
              ...filters,
            })}
            type="text"
            size="middle"
          >
            {t('download.rules')}
          </DownloadButton>
        ),
        key: '2',
      });
    }

    options.push({
      label: (
        <DownloadButton
          id="dowload-algorithms-excel"
          href={expand(AlgorithmsExcelUrl, {
            id,
            algorithmIds: filteredAlgorithmIds,
          })}
          type="text"
          size="middle"
          disabled={
            Array.isArray(filteredAlgorithmIds) &&
            filteredAlgorithmIds.length === 0
          }
        >
          {t('download.algorithms')}
        </DownloadButton>
      ),
      key: '3',
    });

    if (!isInput) {
      options.push({
        label: (
          <DownloadButton
            id="dowload-alternatives-excel"
            href={expand(AlternativesExcelUrl, {
              id,
              ...alternativeFilters,
            })}
            type="text"
            size="middle"
          >
            {t('download.alternatives')}
          </DownloadButton>
        ),
        key: '4',
      });

      if (isReport) {
        const filter = {
          ...requiredKeyFilters,
          validPeriod: requiredKeyFilters.validPeriod?.map((date) =>
            date ? getDayjs(date, DATE_FORMAT)?.format(ISO_DATE_FORMAT) : date,
          ),
        };
        options.push({
          label: (
            <DownloadButton
              id="dowload-keys-excel"
              href={expand(KeysExcelUrl, {
                id,
                ...filter,
              })}
              type="text"
              size="middle"
            >
              {t('download.keys')}
            </DownloadButton>
          ),
          key: '5',
        });
      }
    }

    return options;
  }, [
    alternativeFilters,
    filteredAlgorithmIds,
    filters,
    id,
    isInput,
    isReport,
    requiredKeyFilters,
    t,
  ]);

  return (
    <Dropdown menu={{ items: downloadOptions }} placement="bottom">
      <Button id="dataObject-download" icon={<Icon.Download size={19} />} />
    </Dropdown>
  );
};

const DownloadButton = styled(Button)`
  background-color: transparent;
  &:hover {
    background-color: transparent !important;
  }
  font-weight: ${fontWeightNormal};
  width: 100%;
  display: block;
  padding: ${dropdownPaddingBlock}px ${controlPaddingHorizontal}px;
  justify-content: flex-start !important;
`;
